import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import useSWR from 'swr';
import { AxiosError } from 'axios';
import {
    Anchor,
    Badge,
    Breadcrumbs,
    Button,
    Card,
    Grid,
    Group,
    Image,
    Loader,
    SimpleGrid,
    Skeleton,
    Stack,
    Table,
    Tabs,
    Text,
    Title,
} from '@mantine/core';
import { IconChartBar, IconHistory, IconPhoto, IconCamera, IconApps, IconRefresh } from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import { Device, deviceFetcher } from '../../data/Device';
import { DevicePing, devicePingsFetcher } from '../../data/DevicePing';
import { DevicePingSummary, devicePingSummaryFetcher } from '../../data/PingSummary';
import { capturedImageFetcher, CapturedImages } from '../../data/CapturedImage';
import {
    getDeviceShellSnapshot,
    listComponents,
    reloadDevice,
    type ShellComponent,
} from '../../data/ShellConfig';
import { apiClient } from '../../data/apiClient';
import { useCsrf } from '../../hooks/useCsrf';
import Activity24hChart from '../home/Activity24hChart';
import SportMixDonut from '../home/SportMixDonut';

const formatRelativeTime = (isoString: string): string => {
    const diffMs = Date.now() - new Date(isoString).getTime();
    const diffMins = Math.floor(diffMs / 60_000);
    if (diffMins < 1) return 'just now';
    if (diffMins === 1) return '1 minute ago';
    if (diffMins < 60) return `${diffMins} minutes ago`;
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours === 1) return '1 hour ago';
    if (diffHours < 24) return `${diffHours} hours ago`;
    return formatDateTime(isoString);
};

const formatDateTime = (isoString: string): string =>
    new Date(isoString).toLocaleString(undefined, {
        dateStyle: 'short',
        timeStyle: 'short',
    });

const CAPTURE_POLL_MS = 3000;
const CAPTURE_TIMEOUT_MS = 90_000;

const newestTimestamp = (images: { timestamp: string }[] | undefined): number => {
    if (!images || images.length === 0) return 0;
    const stamps = images
        .map(i => new Date(i.timestamp).getTime())
        .filter(n => Number.isFinite(n));
    return stamps.length > 0 ? Math.max(...stamps) : 0;
};

const DeviceDetail: React.FC = () => {
    const { deviceId } = useParams();
    const navigate = useNavigate();
    const { csrfToken } = useCsrf();
    const [pendingSince, setPendingSince] = useState<number | null>(null);
    const [baselineNewest, setBaselineNewest] = useState<number>(0);
    // Activity history hits device_pings — only fetch when that tab is selected.
    const [activeTab, setActiveTab] = useState<string | null>('overview');

    // latestPing (Live / Currently watching) comes from this payload — refresh so the
    // header does not freeze after the first load now that Activity pings are tab-gated.
    const { data: device, isLoading: deviceLoading, error: deviceError } =
        useSWR<Device, AxiosError>(
            deviceId ? `api/device/${deviceId}` : null,
            deviceFetcher,
            { refreshInterval: 60_000 }
        );

    const { data: pings, isLoading: pingsLoading } =
        useSWR<DevicePing[], AxiosError>(
            deviceId && activeTab === 'activity' ? `api/device/${deviceId}/pings` : null,
            devicePingsFetcher,
            { refreshInterval: 60_000 }
        );

    const { data: pingSummary } = useSWR<DevicePingSummary, AxiosError>(
        deviceId && activeTab === 'overview' ? `api/device/${deviceId}/ping-summary` : null,
        devicePingSummaryFetcher,
        { refreshInterval: 60_000 }
    );

    const {
        data: screenshots,
        isLoading: screenshotsLoading,
        mutate: mutateScreenshots,
    } = useSWR<CapturedImages, AxiosError>(
        deviceId ? `api/device/${deviceId}/captured-images` : null,
        capturedImageFetcher,
        { refreshInterval: pendingSince ? CAPTURE_POLL_MS : 0 }
    );

    const { data: snapshot } = useSWR(
        deviceId ? `api/device-shell/${deviceId}` : null,
        () => getDeviceShellSnapshot(deviceId!)
    );
    const { data: allComponents = [] } = useSWR<ShellComponent[]>('api/components', listComponents);
    const [reloadingId, setReloadingId] = useState<string | null>(null);

    // Unique components this device is currently displaying (deduped across scenes),
    // with human-readable names joined in from the components list (the snapshot omits name).
    const deviceComponents = useMemo(() => {
        const nameById = new Map<string, string>(
            allComponents.map(c => [c.id, c.name] as [string, string])
        );
        const seen = new Map<string, { id: string; name: string; url: string }>();
        snapshot?.playlist.scenes.forEach(scene =>
            scene.panels.forEach(panel => {
                const { id, url } = panel.component;
                if (!seen.has(id)) {
                    seen.set(id, { id, name: nameById.get(id) ?? url, url });
                }
            })
        );
        return Array.from(seen.values());
    }, [snapshot, allComponents]);

    const handleReload = async (componentId: string | null) => {
        if (!deviceId) return;
        setReloadingId(componentId ?? '*');
        try {
            await reloadDevice(deviceId, componentId, csrfToken);
            notifications.show({
                title: 'Refresh requested',
                message: componentId
                    ? 'The device was asked to reload this component.'
                    : 'The device was asked to reload all content.',
            });
        } catch (err: unknown) {
            notifications.show({
                title: 'Refresh failed',
                message: err instanceof Error ? err.message : 'Could not request the refresh.',
                color: 'red',
            });
        } finally {
            setReloadingId(null);
        }
    };

    // When polling reveals a screenshot newer than our baseline, the device has
    // delivered the capture we asked for. Clear pending and surface a toast.
    useEffect(() => {
        if (pendingSince === null) return;
        const newest = newestTimestamp(screenshots);
        if (newest > baselineNewest) {
            setPendingSince(null);
            notifications.show({
                title: 'Screenshot captured',
                message: 'A new screen capture is ready.',
            });
        }
    }, [screenshots, pendingSince, baselineNewest]);

    // Hard timeout so the spinner doesn't hang forever if the device never answers.
    useEffect(() => {
        if (pendingSince === null) return;
        const remaining = CAPTURE_TIMEOUT_MS - (Date.now() - pendingSince);
        if (remaining <= 0) {
            setPendingSince(null);
            return;
        }
        const id = setTimeout(() => {
            setPendingSince(null);
            notifications.show({
                title: 'Still waiting…',
                message: 'The device hasn\'t uploaded a capture yet. It may be offline.',
                color: 'yellow',
            });
        }, remaining);
        return () => clearTimeout(id);
    }, [pendingSince]);

    if (deviceLoading) return <Skeleton height={200} />;
    if (deviceError) return <Text c="red">Error loading device: {deviceError.message}</Text>;
    if (!device) return null;

    const latestPing = device.latestPing ?? null;

    const pingRows = pings?.map((ping) => (
        <Table.Tr key={ping.id}>
            <Table.Td>{ping.currentSport ?? <Text c="dimmed" size="sm">—</Text>}</Table.Td>
            <Table.Td>{ping.imageRoute ?? <Text c="dimmed" size="sm">—</Text>}</Table.Td>
            <Table.Td>
                {ping.isCommercial
                    ? <Badge color="yellow" variant="light">Commercial</Badge>
                    : ping.isSport
                        ? <Badge color="green" variant="light">Sport</Badge>
                        : <Badge color="gray" variant="light">Non-sport</Badge>
                }
            </Table.Td>
            <Table.Td>
                <Text size="sm" c="dimmed">{formatDateTime(ping.createdAt)}</Text>
            </Table.Td>
        </Table.Tr>
    ));

    const handleScreenshotRequest = async () => {
        // Capture the current newest timestamp so we can detect when a new one arrives.
        setBaselineNewest(newestTimestamp(screenshots));
        setPendingSince(Date.now());
        try {
            await apiClient.post(
                `api/device/${deviceId}/request-screenshot`,
                {},
                {
                    headers: {
                        'Content-Type': 'application/json',
                        'X-XSRF-TOKEN': csrfToken,
                    },
                }
            );
        } catch (err: unknown) {
            setPendingSince(null);
            notifications.show({
                title: 'Request failed',
                message: err instanceof Error ? err.message : 'Could not request screenshot.',
                color: 'red',
            });
        }
    };

    return (
        <Stack gap="lg" py="md">
            <Breadcrumbs separatorMargin="xs" separator={<Text size="sm" fw={500}>/</Text>}>
                <Anchor size="sm" fw={500} onClick={() => navigate('/')}>
                    My Screens
                </Anchor>
                <Text size="sm">{device.name}</Text>
            </Breadcrumbs>
            <Group justify="space-between">
                <Title order={2}>{device.name}</Title>
                <Group>
                    <Button variant="default" onClick={() => navigate(`/device/${deviceId}/edit`)}>
                        Edit
                    </Button>
                    <Button variant="default" onClick={() => navigate(`/device/${deviceId}/v2config`)}>
                        Config
                    </Button>
                </Group>
            </Group>

            {device.location && (
                <Text c="dimmed" size="sm">Location: {device.location.name}</Text>
            )}

            <Stack gap="xs">
                <Title order={4}>Currently watching</Title>
                {latestPing ? (
                    <Group gap="md">
                        <Text>
                            {latestPing.currentSport ?? <Text span c="dimmed">Unknown sport</Text>}
                        </Text>
                        {latestPing.isCommercial && <Badge color="yellow" variant="light">Commercial break</Badge>}
                        <Text size="sm" c="dimmed">Last seen {formatRelativeTime(latestPing.createdAt)}</Text>
                    </Group>
                ) : (
                    <Text c="dimmed" size="sm">No pings received yet</Text>
                )}
            </Stack>

            <Tabs value={activeTab} onChange={setActiveTab} keepMounted={false}>
                <Tabs.List>
                    <Tabs.Tab value="overview" leftSection={<IconChartBar size={16} />}>
                        Overview
                    </Tabs.Tab>
                    <Tabs.Tab value="activity" leftSection={<IconHistory size={16} />}>
                        Activity
                    </Tabs.Tab>
                    <Tabs.Tab value="screenshots" leftSection={<IconPhoto size={16} />}>
                        Screenshots
                    </Tabs.Tab>
                    <Tabs.Tab value="content" leftSection={<IconApps size={16} />}>
                        Content
                    </Tabs.Tab>
                </Tabs.List>

                <Tabs.Panel value="overview" pt="md">
                    <Grid gutter="md">
                        <Grid.Col span={{ base: 12, md: 7 }}>
                            <Activity24hChart data={pingSummary?.activityLast24h} />
                        </Grid.Col>
                        <Grid.Col span={{ base: 12, md: 5 }}>
                            <SportMixDonut data={pingSummary?.sportMixToday} />
                        </Grid.Col>
                    </Grid>
                </Tabs.Panel>

                <Tabs.Panel value="activity" pt="md">
                    {pingsLoading ? (
                        <Skeleton height={150} />
                    ) : pings && pings.length > 0 ? (
                        <Table striped highlightOnHover withTableBorder>
                            <Table.Thead>
                                <Table.Tr>
                                    <Table.Th>Sport</Table.Th>
                                    <Table.Th>Image route</Table.Th>
                                    <Table.Th>Status</Table.Th>
                                    <Table.Th>Time</Table.Th>
                                </Table.Tr>
                            </Table.Thead>
                            <Table.Tbody>{pingRows}</Table.Tbody>
                        </Table>
                    ) : (
                        <Text c="dimmed" size="sm">No activity recorded yet</Text>
                    )}
                </Tabs.Panel>

                <Tabs.Panel value="screenshots" pt="md">
                    <Stack gap="md">
                        <Group justify="space-between" align="center">
                            <Text size="sm" c="dimmed">
                                Most recent at the top. Captured screens are uploaded by the device on request.
                            </Text>
                            <Button
                                leftSection={<IconCamera size={16} />}
                                onClick={handleScreenshotRequest}
                                loading={pendingSince !== null}
                                disabled={pendingSince !== null}
                            >
                                {pendingSince !== null ? 'Waiting for device…' : 'Capture Screenshot'}
                            </Button>
                        </Group>
                        {screenshotsLoading ? (
                            <Skeleton height={180} />
                        ) : (
                            <SimpleGrid cols={{ base: 1, sm: 2, md: 3 }} spacing="md">
                                {pendingSince !== null && (
                                    <Card withBorder padding="md" radius="sm">
                                        <Stack align="center" gap="sm" py="lg">
                                            <Loader size="md" />
                                            <Text size="sm" fw={500}>
                                                Capturing screenshot…
                                            </Text>
                                            <Text size="xs" c="dimmed" ta="center">
                                                The device was asked to upload a fresh screen capture.
                                                This usually takes a few seconds.
                                            </Text>
                                        </Stack>
                                    </Card>
                                )}
                                {screenshots?.map((image, index) => (
                                    <Stack key={image.url} gap={4}>
                                        <Image
                                            src={image.url}
                                            alt={`Screenshot ${index}`}
                                            radius="sm"
                                            fit="cover"
                                        />
                                        <Group justify="space-between">
                                            <Text size="xs" c="dimmed">
                                                {image.imageRoute}
                                            </Text>
                                            <Text size="xs" c="dimmed">
                                                {image.timestamp}
                                            </Text>
                                        </Group>
                                    </Stack>
                                ))}
                                {(!screenshots || screenshots.length === 0) && pendingSince === null && (
                                    <Text c="dimmed" size="sm">No screenshots captured yet.</Text>
                                )}
                            </SimpleGrid>
                        )}
                        <Group justify="flex-end">
                            <Button variant="subtle" size="xs" onClick={() => mutateScreenshots()}>
                                Refresh
                            </Button>
                        </Group>
                    </Stack>
                </Tabs.Panel>

                <Tabs.Panel value="content" pt="md">
                    <Stack gap="md">
                        <Group justify="space-between" align="center">
                            <Text size="sm" c="dimmed">
                                Components this device is currently displaying. Refresh reloads the
                                iframe(s) on the screen without changing its config.
                            </Text>
                            <Button
                                leftSection={<IconRefresh size={16} />}
                                variant="light"
                                onClick={() => handleReload(null)}
                                loading={reloadingId === '*'}
                            >
                                Refresh all iframes
                            </Button>
                        </Group>
                        {deviceComponents.length === 0 ? (
                            <Text c="dimmed" size="sm">
                                No components resolved for this device yet.
                            </Text>
                        ) : (
                            <Stack gap="xs">
                                {deviceComponents.map(c => (
                                    <Group
                                        key={c.id}
                                        justify="space-between"
                                        wrap="nowrap"
                                        style={{
                                            padding: 12,
                                            borderRadius: 8,
                                            border: '1px solid var(--mantine-color-gray-4)',
                                        }}
                                    >
                                        <div style={{ minWidth: 0, flex: 1 }}>
                                            <Text fw={600}>{c.name}</Text>
                                            <Text size="xs" c="dimmed" lineClamp={1}>
                                                {c.url}
                                            </Text>
                                        </div>
                                        <Button
                                            variant="light"
                                            color="gray"
                                            size="xs"
                                            leftSection={<IconRefresh size={14} />}
                                            onClick={() => handleReload(c.id)}
                                            loading={reloadingId === c.id}
                                        >
                                            Refresh
                                        </Button>
                                    </Group>
                                ))}
                            </Stack>
                        )}
                    </Stack>
                </Tabs.Panel>
            </Tabs>
        </Stack>
    );
};

export default DeviceDetail;
